import { MoreHorizontal, Search } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

function initials(name) {
  return name.split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}

function shortDate(date) {
  return date ? new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" }).format(new Date(date)) : "—";
}

function providerLabel(provider) {
  return provider === "google" ? "Google" : "Email";
}

export default function EnhancedUserDirectory({
  users,
  actor,
  search,
  onSearch,
  roleFilter,
  onRoleFilter,
  statusFilter,
  onStatusFilter,
  providerFilter,
  onProviderFilter,
  sort,
  onSort,
  onRoleChange,
  onStatusChange,
  onDelete,
  onView,
  onPasswordReset,
  onTransferOwnership,
}) {
  const canManage = (user) => actor.user_id !== user.user_id && (
    actor.role === "owner" || user.role === "member"
  );
  const availableRoles = actor.role === "owner" ? ["admin", "member"] : [];

  return (
    <section className="admin-directory" data-testid="admin-user-directory">
      <div className="admin-directory-toolbar">
        <div>
          <p className="eyebrow">Account directory</p>
          <h2>People in Cardfolio</h2>
        </div>
        <div className="admin-filters">
          <label className="admin-search" data-testid="admin-user-search-field">
            <Search size={16} strokeWidth={1.8} />
            <input value={search} onChange={(event) => onSearch(event.target.value)} placeholder="Search people" data-testid="admin-user-search-input" />
          </label>
          <select value={roleFilter} onChange={(event) => onRoleFilter(event.target.value)} data-testid="role-filter-select">
            <option value="">All roles</option><option value="owner">Owner</option><option value="admin">Admin</option><option value="member">Member</option>
          </select>
          <select value={statusFilter} onChange={(event) => onStatusFilter(event.target.value)} data-testid="status-filter-select">
            <option value="">All statuses</option><option value="active">Active</option><option value="suspended">Suspended</option><option value="invited">Invited</option><option value="pending_verification">Pending verification</option>
          </select>
          <select value={providerFilter} onChange={(event) => onProviderFilter(event.target.value)} data-testid="provider-filter-select">
            <option value="">All providers</option><option value="email">Email</option><option value="google">Google</option>
          </select>
          <select value={sort} onChange={(event) => onSort(event.target.value)} data-testid="directory-sort-select">
            <option value="created_desc">Newest first</option><option value="created_asc">Oldest first</option><option value="last_login_desc">Recent sign-in</option><option value="last_login_asc">Oldest sign-in</option>
          </select>
        </div>
      </div>
      <div className="directory-head directory-head-extended" data-testid="user-directory-header">
        <span>Person</span><span>Role</span><span>Status</span><span>Provider</span><span>Created</span><span>Last login</span><span aria-hidden="true" />
      </div>
      <div className="directory-rows" data-testid="user-directory-rows">
        {users.map((user, index) => {
          const manage = canManage(user);
          return (
            <article className="directory-row directory-row-extended" data-testid={`user-directory-row-${user.user_id}`} key={user.user_id} style={{ animationDelay: `${index * 35}ms` }}>
              <div className="directory-person"><span className="directory-avatar">{initials(user.name)}</span><div><strong data-testid={`directory-user-name-${user.user_id}`}>{user.name}</strong><p data-testid={`directory-user-email-${user.user_id}`}>{user.email}</p></div></div>
              <span className={`role-badge role-${user.role}`} data-testid={`directory-user-role-${user.user_id}`}>{user.role}</span>
              <span className={`status-badge status-${user.account_status}`} data-testid={`directory-user-status-${user.user_id}`}>{user.account_status.replaceAll("_", " ")}</span>
              <span className="directory-meta" data-testid={`directory-user-provider-${user.user_id}`}>{providerLabel(user.auth_provider)}</span>
              <span className="directory-meta" data-testid={`directory-user-created-${user.user_id}`}>{shortDate(user.created_at)}</span>
              <span className="directory-meta" data-testid={`directory-user-last-login-${user.user_id}`}>{shortDate(user.last_login_at)}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="directory-action-button" type="button" aria-label={`Manage ${user.name}`} data-testid={`user-action-menu-${user.user_id}`}><MoreHorizontal size={19} strokeWidth={1.8} /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="admin-action-menu">
                  <DropdownMenuItem onSelect={() => onView(user)} data-testid={`view-user-${user.user_id}`}>View account details</DropdownMenuItem>
                  {manage && <DropdownMenuItem onSelect={() => onPasswordReset(user)} data-testid={`reset-user-password-${user.user_id}`}>Send password reset</DropdownMenuItem>}
                  {manage && availableRoles.filter((role) => role !== user.role).map((role) => <DropdownMenuItem key={role} onSelect={() => onRoleChange(user, role)} data-testid={`set-role-${role}-${user.user_id}`}>Make {role}</DropdownMenuItem>)}
                  {manage && <><DropdownMenuSeparator /><DropdownMenuItem onSelect={() => onStatusChange(user)} data-testid={`toggle-status-${user.user_id}`}>{user.account_status === "active" ? "Suspend account" : "Reactivate account"}</DropdownMenuItem><DropdownMenuItem className="admin-delete-menu-item" onSelect={() => onDelete(user)} data-testid={`delete-user-${user.user_id}`}>Delete permanently</DropdownMenuItem></>}
                  {actor.role === "owner" && actor.user_id !== user.user_id && user.account_status === "active" && <><DropdownMenuSeparator /><DropdownMenuItem onSelect={() => onTransferOwnership(user)} data-testid={`transfer-ownership-${user.user_id}`}>Transfer ownership</DropdownMenuItem></>}
                </DropdownMenuContent>
              </DropdownMenu>
            </article>
          );
        })}
      </div>
    </section>
  );
}